import React, { useContext, useEffect, useRef, useState } from "react";
import { DContext } from "../../../context/Datacontext";
import { FiUploadCloud, FiCamera, FiX } from "react-icons/fi";
import { IoAdd } from "react-icons/io5";
import { useNavigate } from "react-router-dom";

export const MyProfile = () => {

  const { BeURL, instructor, setInstructor , showAlert} = useContext(DContext);
  const fileRef = useRef(null);
  const navigate = useNavigate()

  const [form, setForm] = useState({
    fullName: "",
    headline: "",
    phone: "",
    location: "",
    bio: "",
    skills: []
  });

  const [skillInput, setSkillInput] = useState("");
  const [avatarFile, setAvatarFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  // ================= AUTO FILL =================
  useEffect(() => {
    if (instructor) {
      setForm({
        fullName: instructor.fullName || "",
        headline: instructor.headline || "",
        phone: instructor.contact || "",
        location: instructor.location || "",
        bio: instructor.bio || "",
        skills: instructor.skills || []
      });

      if (instructor.avatar) {
        setPreview(`${BeURL}/uploads/${instructor.avatar}`);
      }
    }
  }, [instructor, BeURL]);

  // ================= HANDLE CHANGE =================
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // ================= SKILLS =================
  const addSkill = () => {
    if (!skillInput.trim()) return;
    if (form.skills.includes(skillInput.trim())) return;

    setForm({
      ...form,
      skills: [...form.skills, skillInput.trim()]
    });

    setSkillInput("");
  };

  const removeSkill = (s) => {
    setForm({
      ...form,
      skills: form.skills.filter(i => i !== s)
    });
  };

  // ================= IMAGE =================
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showAlert("Only image files allowed");
      return;
    }

    setAvatarFile(file);
    setPreview(URL.createObjectURL(file));
  };

  // ================= SUBMIT (UPDATE ONLY) =================
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      const formData = new FormData();

      formData.append("fullName", form.fullName);
      formData.append("headline", form.headline);
      formData.append("phone", form.phone);
      formData.append("location", form.location);
      formData.append("bio", form.bio);

      form.skills.forEach((skill, index) => {
        formData.append(`skills[${index}]`, skill);
      });

      if (avatarFile) {
        formData.append("avatar", avatarFile);
      }

      const res = await fetch(`${BeURL}/update/instructor/profile`, {
        method: "PUT",
        credentials: "include",
        body: formData
      });
      const data = await res.json();

      if (data.success) {
        showAlert("Profile Created Successfully");
        navigate('/instructor/my/basic-info')

        // Update context instantly
        setInstructor(data.updatedInstructor);
      } else {
        showAlert(data.message);
      }

    } catch (err) {
      console.error("Error in create Profile", err);
      showAlert("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl bg-white p-6 rounded-xl shadow-sm">

      <h2 className="text-xl font-semibold mb-6">
        Instructor Profile
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">

        {/* PROFILE PHOTO */}
        <div className="flex flex-col sm:flex-row gap-6 items-start">

          <div className="w-28 h-28 rounded-xl border-2 border-dashed bg-gray-100 flex items-center justify-center overflow-hidden">
            {preview ? (
              <img src={preview} alt="profile" className="w-full h-full object-cover" />
            ) : (
              <FiCamera size={32} className="text-gray-400" />
            )}
          </div>

          <div
            onClick={() => fileRef.current.click()}
            className="flex-1 border-2 border-dashed rounded-xl p-6 text-center cursor-pointer hover:bg-gray-50"
          >
            <FiUploadCloud size={26} className="mx-auto mb-2 text-gray-500" />
            <p className="text-sm font-medium">Upload Profile Photo</p>
            <p className="text-xs text-gray-400">PNG, JPG up to 5MB</p>

            <input
              type="file"
              ref={fileRef}
              onChange={handleImageChange}
              className="hidden"
            />
          </div>
        </div>

        {/* INPUTS */}
        <div className="grid sm:grid-cols-2 gap-4">

          <input
            name="fullName"
            placeholder="Full Name"
            value={form.fullName}
            onChange={handleChange}
            className="border px-3 py-2 rounded-lg"
          />

          <input
            name="headline"
            placeholder="Headline"
            value={form.headline}
            onChange={handleChange}
            className="border px-3 py-2 rounded-lg"
          />

          <input
            name="phone"
            placeholder="Phone"
            value={form.phone}
            onChange={handleChange}
            className="border px-3 py-2 rounded-lg"
          />

          <input
            name="location"
            placeholder="Location"
            value={form.location}
            onChange={handleChange}
            className="border px-3 py-2 rounded-lg"
          />

        </div>

        {/* BIO */}
        <textarea
          name="bio"
          rows="4"
          placeholder="Tell students about yourself..."
          value={form.bio}
          onChange={handleChange}
          className="w-full border px-3 py-2 rounded-lg"
        />

        {/* SKILLS */}
        <div className=" space-y-2">
          <p className="text-sm font-medium mb-2">Skills</p>
          <div className="flex gap-2">
            <input
              value={skillInput}
              onChange={(e) => setSkillInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSkill())}
              placeholder="Add skill and press Enter"
              className="border px-3 py-2 rounded-lg w-full"
            />
            <button
              type="button"
              onClick={addSkill}
              className="flex items-center text-primary-700 font-semibold"
            >
              <IoAdd />
              Add
            </button>
          </div>
          <div className="flex gap-2 flex-wrap mb-2">
            {form.skills.map((s) => (
              <span key={s} className="flex items-center gap-1 bg-primary-600 text-white px-3 py-1 rounded-full text-xs">
                {s}
                <FiX onClick={() => removeSkill(s)} className="cursor-pointer" />
              </span>
            ))}
          </div>
        </div>

        {/* SAVE */}
         <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg"
          >
            {loading ? "Saving..." : "Save"}
          </button>
         </div>

      </form>
    </div>
  );
};
